import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Box, Grid, List, ListItem, Typography, ListItemText } from '@material-ui/core';

import {
  useStyles,
  listTextStyles,
  customCardStyles,
  collapseCardStyles,
} from './CustomerDetails.styled';
import {
  CustomerModal,
  AssignNfcModal,
  DeleteUserModal,
  ConfirmActionModal,
  ChangeUserStatusModal,
  AddCreditsModal,
} from 'components/Modals';
import { usersActions } from 'state/modules/users';
import { Card, CollapseCard } from 'components/UI';
import ItemStatus from 'components/ItemStatus';
import { withRouter } from 'react-router-dom';
import { statusReasons } from 'constants';
import moment from 'moment';
import i18n, { t } from 'i18n-js';
import { buildUrl } from 'utils';
import { useLocation, Link } from 'react-router-dom';
import Tooltip from '@material-ui/core/Tooltip';
import InfoIcon from '@material-ui/icons/Info';
import { accountDeletionStatus } from 'constants/users';
import { shouldRender } from 'utils';

const UserDetails = ({ id, viewOnly }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const listClasses = listTextStyles();
  const collapseCardClasses = collapseCardStyles();
  const cardClasses = customCardStyles();

  const [openNfcModal, setOpenNfcModal] = useState(false);
  const settings = useSelector((state) => state.config?.settings);
  const [openCustomerModal, setOpenCustomerModal] = useState(false);
  const { localeFormat } = useSelector((state) => state.organizations);
  const [openDeleteUserModal, setOpenDeleteUserModal] = useState(false);
  const { user, showConfirmAction } = useSelector((state) => state.users);
  const isCustomer = user?.is_customer ?? true;
  const optOutEnable = settings?.core?.organization?.opt_out_enable;
  const [openConfirmActionModal, setOpenConfirmActionModal] = useState(false);
  const bastion =
    useSelector((state) => state?.organizations?.organization?.bastion?.id_slug) || '';
  const { account } = useSelector((state) => state.customers || {});
  const hasPricingCredits = useSelector(
    (state) => state.config?.settings?.core?.pricing?.credits || false
  );
  const [openCreditModal, setOpenCreditModal] = useState(false);
  const [openStatusModal, setOpenStatusModal] = useState(false);

  const location = useLocation();
  const routeTo = buildUrl(`/customers/${id}`);

  const title =
    location?.pathname === routeTo ? (
      <>ID# {id || ''}</>
    ) : (
      <Link to={routeTo}>
        <span className={classes.link}>ID# {id || ''}</span>
      </Link>
    );

  useEffect(() => {
    if (id) {
      dispatch(usersActions.fetchUserById(id));
    } else {
      dispatch(usersActions.resetUser());
    }
  }, [id]); // eslint-disable-line

  useEffect(() => {
    if (showConfirmAction) {
      handleDeleteActiomModal();
    }
  }, [showConfirmAction]); // eslint-disable-line

  /**
   * Open change user modal
   */
  const handleCustomerModal = () => {
    setOpenCustomerModal((prevState) => !prevState);
  };

  /**
   * handle change status modal
   */
  const handleChangeStatusModal = () => {
    setOpenStatusModal((prevState) => !prevState);
  };

  /**
   * handle add credits modal
   */
  const handleAddCreditsModal = () => {
    setOpenCreditModal((prevState) => !prevState);
  };

  /**
   * Open change user status modal
   */
  const handleNfcModal = () => {
    setOpenNfcModal((prevState) => !prevState);
  };

  /**
   * Open confirm deleta user
   */
  const handleDeleteActiomModal = () => {
    setOpenConfirmActionModal((prevState) => !prevState);
  };

  /**
   * Send confirmation link
   */
  const handleConfirmationLink = () => {
    if (id && user) {
      dispatch(usersActions.fetchSendConfirmationLink(id, user.email));
    }
  };

  /**
   * Send password reset link
   */
  const handleSendPasswordResetLink = () => {
    if (id && user && bastion) {
      dispatch(usersActions.fetchSendPasswordReset(user.email, bastion));
    }
  };

  /**
   * Send mobile phone confirm SMS
   */
  const handleSendPhoneConfirmSMS = () => {
    if (id && user) {
      dispatch(usersActions.fetchSendPhoneConfirmationSMS(id, user.mobile_phone));
    }
  };

  /**
   * handle Delete user modal
   */
  const handleDeleteUserModal = () => {
    setOpenDeleteUserModal((prevState) => !prevState);
  };

  /**
   * handle accept customer deletion
   */
  const handleAcceptCustomerDeletion = () => {
    setOpenDeleteUserModal((prevState) => !prevState);
  };

  /**
   * handle reject customer deletion
   */
  const handleRejectCustomerDeletion = () => {
    if (id) {
      dispatch(
        usersActions.fetchDeletionUser(id, {
          deletion_status: accountDeletionStatus.REJECTED,
        })
      );
    }
  };

  /**
   * Vertical menu list
   * It has the label and the function for each action
   */
  const items = [
    {
      render: !viewOnly,
      can: { action: 'update', name: 'user' },
      'data-cy': 'update-customer-btn',
      title: i18n.t('users.update_customer_info'),
      onClick: handleCustomerModal,
    },
    {
      render: !viewOnly,
      can: { action: 'update_status', name: 'user' },
      'data-cy': 'change-customer-status-btn',
      title: i18n.t('users.change_status'),
      onClick: handleChangeStatusModal,
      divider: true,
    },
    {
      title: i18n.t('users.confirmation_link'),
      onClick: handleConfirmationLink,
    },
    {
      title: i18n.t('users.reset_pwd_link'),
      onClick: handleSendPasswordResetLink,
      'data-cy': 'pwd-reset-link-btn',
    },
    {
      title: i18n.t('users.confirm_sms'),
      onClick: handleSendPhoneConfirmSMS,
      enabled: !!user?.mobile_phone,
      divider: true,
    },
    {
      title: i18n.t('users.assign_nfc'),
      feature: { name: 'key', is: 'enabled' },
      onClick: handleNfcModal,
      divider: !hasPricingCredits,
    },
    {
      render:
        !viewOnly &&
        shouldRender({
          accessControl: ['configuration.pricing.credits.true', 'AND', 'privilege.credit.create'],
          configuration: settings?.core,
        }),
      'data-cy': 'add-credits-btn',
      title: i18n.t('customers.add_credits'),
      onClick: () => handleAddCreditsModal('credit'),
      divider: true,
    },
    {
      render: user?.deletion_status === accountDeletionStatus.REQUESTED,
      can: { action: 'delete', name: 'user' },
      'data-cy': 'accept-customer-btn',
      title: i18n.t('customers.accept_customer_deletion'),
      onClick: handleAcceptCustomerDeletion,
    },
    {
      render: user?.deletion_status === accountDeletionStatus.REQUESTED,
      can: { action: 'delete', name: 'user' },
      'data-cy': 'reject-customer-btn',
      title: i18n.t('customers.reject_customer_deletion'),
      onClick: handleRejectCustomerDeletion,
    },
    {
      render: user?.deletion_status !== accountDeletionStatus.REQUESTED,
      can: { action: 'delete', name: 'user' },
      title: i18n.t('customers.delete'),
      onClick: handleDeleteUserModal,
    },
  ];

  /**
   * Parse user data
   * @param {object} user Object with user data
   */
  const getUserData = (user = {}) => {
    const hasBusinessInformation = Object.keys(user?.extra_fields || {})?.length ? true : false;
    const userInfo = [
      ...(hasBusinessInformation
        ? [
            {
              title: i18n.t('customers.business_information'),
              items: [
                {
                  label: `${i18n.t('customers.federal_id')}:`,
                  value: user?.extra_fields?.federal_id || '-',
                },
                {
                  label: `${i18n.t('customers.us_dot')}:`,
                  value: user?.extra_fields?.us_dot_id || '-',
                },
                {
                  label: `${i18n.t('customers.doing_business_as')}:`,
                  value: user?.extra_fields?.dba || '-',
                },
                {
                  label: `${i18n.t('customers.organization.organization_state')}:`,
                  value: user?.extra_fields?.organization_state || '-',
                },
                {
                  label: `${i18n.t('customers.organization.organization_type')}:`,
                  value: user?.extra_fields?.organization_type || '-',
                },
              ],
            },
          ]
        : []),
      {
        title: i18n.t('customers.basic_information'),
        dataCy: 'customer-basic-info',
        items: [
          {
            label: `${i18n.t('common.full_name')}:`,
            value: `${user?.first_name || ''} ${user?.last_name || ''}` || '-',
          },
          { label: `${i18n.t('common.date_of_birth')}:`, value: user?.date_of_birth || '-' },
          { label: `${i18n.t('common.role')}:`, value: user?.role || '-', classes: 'capitalize' },
          { label: `${i18n.t('common.invite_code')}:`, value: user?.invite_code || '-' },
        ],
      },
      {
        title: i18n.t('customers.contact_information'),
        dataCy: 'customer-contact-info',
        items: [
          {
            label: `${i18n.t('common.phone')}:`,
            value: user?.mobile_phone || '-',
            confirmed: user?.mobile_phone
              ? user.mobile_phone_confirmed_at || i18n.t('common.unconfirmed')
              : '',
          },
          {
            label: `${i18n.t('common.email')}:`,
            value: user?.email || '-',
            confirmed: user?.email_confirmed_at || i18n.t('common.unconfirmed'),
          },
          { label: `${i18n.t('common.city')}:`, value: user?.address?.city || '-' },
          { label: `${i18n.t('common.state_province')}:`, value: user?.address?.state || '-' },
          {
            label: `${i18n.t('common.address')}:`,
            value: user?.address?.street_address_line_1
              ? `${user?.address?.street_address_line_1}${
                  user?.address?.street_address_line_2
                    ? `, ${user?.address?.street_address_line_2}`
                    : ''
                }`
              : '-',
          },
          { label: `${i18n.t('common.zip_code')}:`, value: user?.address?.zip_code || '-' },
        ],
      },
      {
        title: i18n.t('customers.driver_license_information'),
        dataCy: 'customer-driver-license-info',
        items: [
          {
            label: `${i18n.t('customers.driver_license_id')}:`,
            value: user?.driver_license?.license_id || '-',
          },
          {
            label: `${i18n.t('customers.license_expiration_date')}:`,
            value: user?.driver_license?.license_expiration || '-',
          },
          {
            label: `${i18n.t('customers.driver_license_state')}:`,
            value: user?.driver_license?.license_state || '-',
          },
          {
            label: `${i18n.t('customers.jurisdiction')}:`,
            value: user?.driver_license?.jurisdiction_vehicle_class || '-',
          },
        ],
      },
      {
        dataCy: 'customer-additional-information',
        title: i18n.t('customers.additional_information.title'),
        items: [
          {
            label: `${i18n.t('customers.internal_id')}:`,
            value: user?.internal_id || '-',
            dataCy: 'customer_internal_id',
            tooltip: i18n.t('customers.additional_information.tooltip'),
          },
          {
            label: `${i18n.t('customers.email_subscription_status')}:`,
            value: user?.email_opt_out
              ? `${i18n.t('subscription_status.unsubscribed')}`
              : `${i18n.t('subscription_status.subscribed')}`,
            dataCy: 'customer_email_subscription_status',
            visible: optOutEnable && isCustomer,
          },
          {
            label: `${i18n.t('customers.sms_subscription_status')}:`,
            value: user?.sms_opt_out
              ? `${i18n.t('subscription_status.unsubscribed')}`
              : `${i18n.t('subscription_status.subscribed')}`,
            dataCy: 'customer_sms_subscription_status',
            visible: optOutEnable && isCustomer,
          },
        ],
      },
    ];

    return userInfo;
  };

  /**
   * Parse user status data
   * @param {object} user Object with user status data
   */
  const getUserStatusData = (user = {}) => {
    const statusDescription = statusReasons?.find(
      (status) => status.value === user?.status_details?.[0]
    );
    const userInfo = [
      { label: `${i18n.t('users.reason')}:`, value: statusDescription?.label || '-' },
      { label: 'Description:', value: user?.status_notes || '-' },
    ];

    return userInfo;
  };

  return (
    <Card
      menuItems={viewOnly ? [] : items}
      menuPermissions={{
        action: 'update',
        subject: 'user',
        type: 'can',
      }}
      title={i18n.t('customers.customer_information')}
      headerActionType="vertical"
      contentRootClasses={cardClasses}
      data-cy="customers-widget-card"
    >
      {id ? (
        <>
          <Box className={classes.cardContentPadding}>
            <Grid container direction="row" alignItems="center" spacing={1}>
              <Grid item xs={8}>
                <Typography variant="h3">
                  <Box className={classes.name}>{title}</Box>
                </Typography>
              </Grid>
              {user?.status && (
                <Grid item xs={12}>
                  <ItemStatus
                    label={i18n.t('common.status')}
                    variant="labelValue"
                    i18nKey="userStatus"
                    status={user?.status}
                  />
                </Grid>
              )}
              {user?.deletion_status && (
                <Grid item xs={12}>
                  <ItemStatus
                    variant="labelValue"
                    i18nKey="deletionStatus"
                    status={user.deletion_status}
                  />
                </Grid>
              )}
            </Grid>

            {user?.status && user?.status !== 'ACTIVE' && (
              <List className={classes.userList}>
                {getUserStatusData(user).map((val, index) => (
                  <ListItem key={index} className={classes.userListItem}>
                    <ListItemText
                      classes={listClasses}
                      primary={val.label}
                      secondary={
                        <Box
                          display="flex"
                          component="span"
                          flexDirection="column"
                          className={classes.textRight}
                        >
                          <Typography
                            variant="body2"
                            component="span"
                            color="textSecondary"
                            className={classes?.[val.classes]}
                            data-cy="customer_list"
                          >
                            {val.value}
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            )}
            <div>
              {getUserData(user).map((val, index) => (
                <CollapseCard
                  key={index}
                  title={val.title}
                  dataCy={val.dataCy}
                  customClasses={collapseCardClasses}
                  tooltip={val.tooltip}
                >
                  <List className={classes.userList}>
                    {val.items.map((val, index) => {
                      if (
                        !val.hasOwnProperty('visible') ||
                        (val.hasOwnProperty('visible') && val.visible)
                      ) {
                        return (
                          <ListItem key={index} className={classes.userListItem}>
                            <ListItemText
                              classes={listClasses}
                              primary={
                                <Box display="flex" alignItems="center">
                                  {val.label}
                                  {val?.tooltip ? (
                                    <Tooltip title={val.tooltip}>
                                      <InfoIcon className={classes.infoIcon} />
                                    </Tooltip>
                                  ) : null}
                                </Box>
                              }
                              secondary={
                                <Box
                                  display="flex"
                                  component="span"
                                  flexDirection="column"
                                  className={classes.textRight}
                                >
                                  <Typography
                                    variant="body2"
                                    component="span"
                                    color="textSecondary"
                                    data-cy={val.dataCy}
                                  >
                                    {val.value}
                                  </Typography>
                                  {val.hasOwnProperty('confirmed') &&
                                    val.confirmed !== i18n.t('common.unconfirmed') &&
                                    moment(val.confirmed).isValid() && (
                                      <Typography
                                        variant="body2"
                                        component="span"
                                        className={classes.confirmed}
                                      >
                                        {i18n.t('users.confirmed')}:{' '}
                                        {moment(val.confirmed).format(`${localeFormat} hh:mm a`)}
                                      </Typography>
                                    )}
                                  {val.hasOwnProperty('confirmed') &&
                                    val.confirmed === i18n.t('common.unconfirmed') && (
                                      <Typography
                                        variant="body2"
                                        component="span"
                                        className={classes.unconfirmed}
                                      >
                                        {val.confirmed}
                                      </Typography>
                                    )}
                                </Box>
                              }
                            />
                          </ListItem>
                        );
                      } else {
                        return null;
                      }
                    })}
                  </List>
                </CollapseCard>
              ))}
            </div>
          </Box>
          {openCustomerModal && (
            <CustomerModal
              id={id}
              user={user}
              open={openCustomerModal}
              handleClick={handleCustomerModal}
            />
          )}
          {openDeleteUserModal && (
            <DeleteUserModal
              id={id}
              user={user}
              type="customer"
              open={openDeleteUserModal}
              handleClick={handleDeleteUserModal}
            />
          )}
          {openConfirmActionModal && (
            <ConfirmActionModal
              id={id}
              user={user}
              type="customer"
              open={openConfirmActionModal}
              handleClick={handleDeleteActiomModal}
            />
          )}
          {openNfcModal && (
            <AssignNfcModal id={id} user={user} open={openNfcModal} handleClick={handleNfcModal} />
          )}
          {openStatusModal && (
            <ChangeUserStatusModal
              id={id}
              open={openStatusModal}
              status={user?.status}
              handleClick={handleChangeStatusModal}
              userType="CUSTOMER"
            />
          )}
          {openCreditModal && (
            <AddCreditsModal
              to="driver"
              type="credit"
              account={account}
              user={user}
              accountLevel={true}
              open={openCreditModal}
              handleClick={handleAddCreditsModal}
            />
          )}
        </>
      ) : (
        <Box className={classes.cardContentPadding}>{i18n.t('customers.undefined')}</Box>
      )}
    </Card>
  );
};

export default withRouter(UserDetails);
